import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Phone, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdminLeads() {
  const [sp, setSp] = useSearchParams();
  const initialToken = sp.get("token") || localStorage.getItem("admin_token") || "";
  const [token, setToken] = useState(initialToken);
  const [inputToken, setInputToken] = useState(initialToken);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async (t) => {
    if (!t) return false;
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { token: t, limit: 500 } });
      setItems(data.items || []);
      setCount(data.count || 0);
      localStorage.setItem("admin_token", t);
      return true;
    } catch (err) {
      const msg =
        err?.response?.status === 401
          ? "Invalid admin token."
          : "Could not load leads.";
      toast.error(msg);
      setItems([]);
      setCount(0);
      // Reset token so the auth form is shown again on 401
      if (err?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        setToken("");
        setSp({});
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialToken) load(initialToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doAuth = async (e) => {
    e.preventDefault();
    const ok = await load(inputToken);
    if (ok) {
      setToken(inputToken);
      setSp({ token: inputToken });
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1200);
  };

  const fmt = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-ink-mist flex items-center justify-center px-5">
        <form
          data-testid="admin-auth-form"
          onSubmit={doAuth}
          className="w-full max-w-sm bg-white border border-ink-line p-8"
        >
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-3">
            Urban Painter · Admin
          </div>
          <h1 className="font-display text-2xl text-ink mb-6">
            Leads Dashboard
          </h1>
          <Label className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted">
            Admin Token
          </Label>
          <Input
            data-testid="admin-token-input"
            type="password"
            value={inputToken}
            onChange={(e) => setInputToken(e.target.value)}
            placeholder="Enter admin token"
            className="mt-2 h-12 rounded-none border-ink-line focus-visible:ring-ink focus-visible:ring-1"
          />
          <button
            type="submit"
            data-testid="admin-auth-submit"
            className="mt-5 w-full bg-ink hover:bg-ink-soft text-white h-12 text-[11px] tracking-[0.25em] uppercase"
          >
            View Leads
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-24 pb-16">
      <div className="max-w-[1400px] mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 border-b border-ink-line pb-6">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-ink-muted mb-3">
              Admin · Leads Feed
            </div>
            <h1 className="font-display text-3xl md:text-4xl text-ink leading-tight">
              All enquiries{" "}
              <span className="font-serif-italic font-normal">
                (from urbanpainter.in)
              </span>
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              {count} lead{count === 1 ? "" : "s"} · newest first
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh"
              onClick={() => load(token)}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-ink-line px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase text-ink hover:border-ink"
            >
              {loading && <Loader2 size={12} className="animate-spin" />}
              Refresh
            </button>
            <button
              data-testid="admin-logout"
              onClick={() => {
                localStorage.removeItem("admin_token");
                setToken("");
                setInputToken("");
                setItems([]);
                setSp({});
              }}
              className="text-[11px] tracking-[0.25em] uppercase text-ink-muted hover:text-ink"
            >
              Sign out
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div data-testid="admin-empty" className="py-24 text-center text-ink-muted">
            {loading ? "Loading…" : "No leads yet."}
          </div>
        ) : (
          <div className="overflow-x-auto border border-ink-line">
            <table className="w-full text-sm">
              <thead className="bg-ink-mist">
                <tr className="text-left font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted">
                  <Th>When</Th>
                  <Th>Name</Th>
                  <Th>Mobile</Th>
                  <Th>Service</Th>
                  <Th>Property</Th>
                  <Th>Location</Th>
                  <Th>Area</Th>
                  <Th>Type</Th>
                  <Th>Contact</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`admin-lead-${l.id}`}
                    className="border-t border-ink-line hover:bg-ink-mist/50 transition-colors"
                  >
                    <Td className="whitespace-nowrap text-xs text-ink-muted">
                      {fmt(l.created_at)}
                    </Td>
                    <Td className="font-medium text-ink">{l.name || "-"}</Td>
                    <Td>
                      <button
                        onClick={() => copyToClipboard(l.phone, `p-${l.id}`)}
                        className="inline-flex items-center gap-2 hover:text-ink group"
                      >
                        {l.phone || "-"}
                        {l.phone && (
                          <span className="text-ink-muted group-hover:text-ink">
                            {copied === `p-${l.id}` ? (
                              <Check size={12} />
                            ) : (
                              <Copy size={12} />
                            )}
                          </span>
                        )}
                      </button>
                    </Td>
                    <Td className={l.service === "Epoxy Flooring" ? "text-orange font-medium" : ""}>
                      {l.service || "-"}
                    </Td>
                    <Td className="text-ink-mid">{l.property_type || "-"}</Td>
                    <Td className="text-ink-mid">{l.property_location || "-"}</Td>
                    <Td className="text-ink-mid whitespace-nowrap">{l.area || "-"}</Td>
                    <Td className="text-xs text-ink-muted whitespace-nowrap">
                      {(l.lead_type || "").replace(/_/g, " ")}
                    </Td>
                    <Td>
                      <div className="flex items-center gap-2">
                        {l.phone && (
                          <>
                            <a
                              href={`tel:${l.phone.replace(/[^0-9+]/g, "")}`}
                              className="p-1.5 border border-ink-line hover:bg-ink hover:text-white hover:border-ink transition-colors"
                              title="Call"
                            >
                              <Phone size={12} />
                            </a>
                            <a
                              href={`https://wa.me/${l.phone.replace(/[^0-9]/g, "")}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 border border-ink-line hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors"
                              title="WhatsApp"
                            >
                              <MessageCircle size={12} />
                            </a>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-3 py-3 whitespace-nowrap">{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`px-3 py-3 align-middle ${className}`}>{children}</td>;
}
